<script>
    import MBaseFormElement from "@/assets/plugins/mps-form/providers/MBaseFormElement";
    import {ETC} from "@/constants/etc-code";
    import {isNOU} from "@/assets/plugins/mps-common/expansions/condition";
    import fileManager from "@/assets/plugins/mps-common/file/file-manager";
    import {SCROLL_STATE} from "@/assets/plugins/mps-mobile/event/scroll-detector-mobile";

    export default {
        name: "MBaseFormFileUpload",
        extends: MBaseFormElement,
        data() {
            return {
                attachments: undefined,
                files: [],
                filesAppend: undefined,
            };
        },
        computed: {
            accept() {
                return this.options.accept || "*";
            },

            filesWithId() {
                let i = 0;
                if (this.$isEmpty(this.files) === true) return [];
                const arr = [];
                for (let index = 0; index < this.files.length; index++) {
                    const e = this.files[index];
                    const obj = {
                        id: this.formKey + "-" + "file-" + index,
                        file: e,
                    }
                    arr.push(obj);
                }
                return arr;
            },
        },
        watch: {
            filesAppend() {
              this.resizeFilesAppend()
            },

            filesWithId() {
                if (this.$isEmpty(this.filesWithId) === true) return;
                this.filesWithId.forEach(f => {
                    const reader = new FileReader();

                    reader.onload = function (e) {
                        const el = document.getElementById(f.id)
                        if (!!el) {
                            el.setAttribute("src", e.target.result);
                        }
                    }

                    reader.readAsDataURL(f.file); // convert to base64 string
                });
            },

            "options.attachments"() {
                this.clone();
            },

            inEditMode() {
                this.init();
            },
        },
        created() {
            this.clone();
        },
        methods: {
            init() {
                if (Array.isArray(this.attachments) !== true) return;
                this.attachments.map(e => e.init());
            },

            clone() {
                this.files = [];
                if (Array.isArray(this.options.attachments) !== true) return;
                this.attachments = this.options.attachments.map(e => e.clone());
                let i = 0;
                this.attachments.forEach(e => {
                     if (this.fileManager.isImageFile(e.serverFile.path)) e.imageIndex = i++;
                });
                if (this.options.listType === ETC.MULTIFILE_FORM_LIST_TYPE.IMAGE_TYPE) {
                    this.attachments.forEach(e => {
                        e.nullize();
                        e.base64();
                    });
                }
            },

          async resizeFilesAppend() {

            if (this.$isEmpty(this.filesAppend) === true) return;
            this.$snackbar(this.$translate('이미지 파일의 사이즈를 변환중입니다.'), 3000)

            this._timeout = setTimeout(() => {
              this.$snackbar(this.$translate('잠시만 기다려 주세요.'), 3000)
            }, 3100);
            const resizeFile = await this.resizeAsImagesIfNeeded(this.filesAppend)
            clearTimeout(this._timeout)
            this.files.push(...resizeFile)
          },

          /**
           * 첨부된 파일 배열에서 이미지 파일만 선별해, 더 긴 변이 maxDim을 초과하면
           * 비율을 유지하여 더 긴 변을 maxDim으로 맞춰 리사이즈합니다.
           */
          async resizeAsImagesIfNeeded(files, maxDim = 1000) {
            if (!files || !files.length) return [];
            const result = [];
            for (const f of files) {
              // File 혹은 Blob 형태가 아니거나, 이미지가 아니면 그대로 사용
              const type = (f && f.type) || '';
              if (!(f instanceof File || f instanceof Blob) || !type.startsWith('image/')) {
                result.push(f);
                continue;
              }
              try {
                const resized = await this.resizeImageFile(f, maxDim);
                result.push(resized || f);
              } catch (e) {
                console.warn('파일 리사이즈 실패, 원본 사용:', e);
                result.push(f);
              }
            }
            return result;
          },

          /**
           * 단일 이미지 파일 리사이즈
           * - 더 긴 변이 maxDim 이하이면 원본 반환
           * - 초과 시 canvas로 다운스케일 후 동일 mimetype으로 File 반환
           */
          async resizeImageFile(file, maxDim = 1000) {
            const img = await this.loadImageFromFile(file);
            const { width, height } = img;
            const maxSide = Math.max(width, height);
            if (maxSide <= maxDim) {
              // 리사이즈 불필요
              return file;
            }

            const scale = maxDim / maxSide;
            const targetW = Math.round(width * scale);
            const targetH = Math.round(height * scale);

            const canvas = document.createElement('canvas');
            canvas.width = targetW;
            canvas.height = targetH;
            const ctx = canvas.getContext('2d');

            // 고해상도 스케일링 품질 향상 옵션
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';

            ctx.drawImage(img, 0, 0, targetW, targetH);

            const mime = file.type && file.type.startsWith('image/') ? file.type : 'image/jpeg';
            const quality = 0.92; // 기본 품질
            const blob = await new Promise((resolve, reject) => {
              canvas.toBlob(b => (b ? resolve(b) : reject(new Error('toBlob failed'))), mime, quality);
            });

            // File 이름 유지 (Blob일 경우 이름이 없으므로 기본값 사용)
            const fileName = file.name || 'image';
            return new File([blob], fileName, { type: mime, lastModified: Date.now() });
          },

          /**
           * File/Blob을 ImageBitmap 혹은 HTMLImageElement로 로드
           */
          async loadImageFromFile(file) {
            // createImageBitmap 사용 가능 시 빠르게 로드
            if (window.createImageBitmap) {
              const bitmap = await createImageBitmap(file);
              // drawImage는 ImageBitmap도 지원하므로 그대로 반환
              return bitmap;
            }
            // 폴백: HTMLImageElement 로드
            const url = URL.createObjectURL(file);
            try {
              const imgEl = await new Promise((resolve, reject) => {
                const img = new Image();
                img.onload = () => resolve(img);
                img.onerror = reject;
                img.src = url;
              });
              return imgEl;
            } finally {
              URL.revokeObjectURL(url);
            }
          },
        },
    }
</script>